import { useMemo, type CSSProperties } from 'react';

import { cn } from '../css/className/className';

import './layoutStyle.css';

const cls = cn('Layout');

/** CSS-строка с единицами измерения, например `'200px'`, `'50%'`, `'100vw'`, `'100vh'`, `'100dvh'`. */
export type LayoutUnitSizeValue = `${number}${'px' | '%' | 'vw' | 'vh' | 'dvh'}`;

/**
 * Семантические значения размера: `'auto'` (по содержимому) и `'max'` (100% родителя).
 */
export type LayoutSemanticSizeValue = 'auto' | 'max';

/**
 * Полный набор допустимых значений размера:
 * семантические токены (`'auto'`, `'max'`) и unit-строки.
 */
export type FullLayoutSizeValue = LayoutSemanticSizeValue | LayoutUnitSizeValue;

/**
 * Базовые layout-пропсы.
 *
 * Используются как «опорная» форма пропсов для layout-хелперов
 *
 * Компоненты, желающие сузить допустимые значения отдельных полей,
 * импортируют один из готовых пресетов (например `FullLayoutProps`)
 * и передают совместимые значения в эти хелперы.
 */
interface LayoutStyleProps {
    /**
     * Ширина элемента.
     * - `'auto'` — по содержимому (`fit-content`)
     * - `'max'` — растянуть на 100% родителя (`width: 100%`)
     * - строка с единицами измерения, например `'200px'`, `'50%'`, `'100vw'`, `'100vh'`
     */
    width?: FullLayoutSizeValue;

    /**
     * Высота элемента.
     * - `'auto'` — по содержимому (`fit-content`)
     * - `'max'` — растянуть на 100% родителя (`height: 100%`)
     * - строка с единицами измерения, например `'200px'`, `'50%'`, `'100vw'`, `'100vh'`
     */
    height?: LayoutSemanticSizeValue | LayoutUnitSizeValue;

    /**
     * Минимальная ширина элемента — строка с единицами измерения
     * (`'200px'`, `'50%'`, `'100vw'`, `'100vh'`).
     */
    minWidth?: LayoutUnitSizeValue;

    /**
     * Максимальная ширина элемента — строка с единицами измерения
     * (`'200px'`, `'50%'`, `'100vw'`, `'100vh'`).
     */
    maxWidth?: LayoutUnitSizeValue;

    /**
     * Минимальная высота элемента — строка с единицами измерения
     * (`'200px'`, `'50%'`, `'100vw'`, `'100vh'`).
     */
    minHeight?: LayoutUnitSizeValue;

    /**
     * Максимальная высота элемента — строка с единицами измерения
     * (`'200px'`, `'50%'`, `'100vw'`, `'100vh'`).
     */
    maxHeight?: LayoutUnitSizeValue;

    /**
     * CSS-свойство flex (сокращённая запись), например `'1 1 auto'`, `'0 0 200px'`.
     */
    flex?: string;
}

/**
 * Результат вычисления layout-стилей: inline-`style` для unit-значений
 * и `className` с пресетами для семантических значений (`auto`, `max`).
 */
export interface LayoutStyleResult {
    style: CSSProperties;
    className: string;
}

const SEMANTIC_WIDTH_VALUES = new Set(['auto', 'max']);
const SEMANTIC_HEIGHT_VALUES = new Set(['auto', 'max']);

/**
 * Является ли значение ширины семантическим (`'auto'`/`'max'`).
 * Такие значения реализуются CSS-классами, а не inline-стилями.
 */
export const isSemanticWidthValue = (value: LayoutStyleProps['width']): value is LayoutSemanticSizeValue =>
    typeof value === 'string' && SEMANTIC_WIDTH_VALUES.has(value);

const isSemanticHeightValue = (value: LayoutStyleProps['height']): value is LayoutSemanticSizeValue =>
    typeof value === 'string' && SEMANTIC_HEIGHT_VALUES.has(value);

export const getLayoutStyle = (props: Partial<LayoutStyleProps>): LayoutStyleResult => {
    const { width, height, minWidth, maxWidth, minHeight, maxHeight, flex } = props;

    const isSemanticWidth = isSemanticWidthValue(width);
    const isSemanticHeight = isSemanticHeightValue(height);

    // Для семантических значений ширины/высоты сами размеры применяются через
    // CSS-пресеты, поэтому в inline-style они не попадают.
    const style: CSSProperties = {
        width: isSemanticWidth ? undefined : width,
        height: isSemanticHeight ? undefined : height,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        flex,
    };

    const className = cls({
        width: isSemanticWidth ? width : undefined,
        height: isSemanticHeight ? height : undefined,
    });

    return { style, className };
};

export const useLayoutStyle = (props: Partial<LayoutStyleProps>): LayoutStyleResult => {
    const { width, height, minWidth, maxWidth, minHeight, maxHeight, flex } = props;

    return useMemo(
        () => getLayoutStyle({ width, height, minWidth, maxWidth, minHeight, maxHeight, flex }),
        [flex, height, maxHeight, maxWidth, minHeight, minWidth, width],
    );
};

// =====================================================================
// Пресеты layout-пропсов
// =====================================================================
//
// Готовые именованные пресеты layout-пропсов с заранее сужёнными типами
// для каждого поля. Компоненты импортируют нужный пресет и используют
// его как часть собственных пропсов — не описывая литералы повторно.

/**
 * Полный пресет layout-пропсов:
 * - `width`, `height` — семантические токены (`'auto'`, `'max'`) и unit-строки.
 * - `min/maxWidth`, `min/maxHeight` — только unit-строки.
 * - `flex` — CSS-сокращение строкой.
 *
 * @example
 * import type { LayoutComponentsLayoutProps } from '../../lib/layoutStyle/layoutStyle';
 *
 * interface MyProps extends LayoutComponentsLayoutProps {
 *     // ...other props
 * }
 */
export interface LayoutComponentsLayoutProps {
    /** Ширина элемента. */
    width?: FullLayoutSizeValue;
    /** Минимальная ширина элемента — строка с единицами измерения. */
    minWidth?: LayoutUnitSizeValue;
    /** Максимальная ширина элемента — строка с единицами измерения. */
    maxWidth?: LayoutUnitSizeValue;
    /** Высота элемента. */
    height?: LayoutSemanticSizeValue | LayoutUnitSizeValue;
    /** Минимальная высота элемента — строка с единицами измерения. */
    minHeight?: LayoutUnitSizeValue;
    /** Максимальная высота элемента — строка с единицами измерения. */
    maxHeight?: LayoutUnitSizeValue;
    /** CSS-свойство flex (сокращённая запись), например `'1 1 auto'`. */
    flex?: string;
}

/**
 * Пресет layout-пропсов для контролов (Select, Input и т.п.):
 * управление шириной по горизонтали, без высоты.
 * - `width` — семантические токены (`'auto'`, `'max'`) и unit-строки.
 * - `min/maxWidth` — только unit-строки.
 * - `flex` — CSS-сокращение строкой.
 *
 * @example
 * import type { LayoutControlsLayoutProps } from '../../lib/layoutStyle/layoutStyle';
 *
 * interface MyControlProps extends LayoutControlsLayoutProps {
 *     // ...other props
 * }
 */
export interface LayoutControlsLayoutProps {
    /** Ширина контрола. */
    width?: FullLayoutSizeValue;
    /** Минимальная ширина контрола — строка с единицами измерения. */
    minWidth?: LayoutUnitSizeValue;
    /** Максимальная ширина контрола — строка с единицами измерения. */
    maxWidth?: LayoutUnitSizeValue;
    /** CSS-свойство flex (сокращённая запись), например `'1 1 auto'`. */
    flex?: string;
}
